<template>
  <div class="view" v-if="canView || canEdit || canDelete" v-cloak>
    <GridPanelHeader
      :title="$t('stock_management.product_types.title')"
      :grid-ref="$refs.smProductTypesGrid"
      :show-add="canEdit"
      :show-filter="false"
      @add="newObject"
    />

    <DxDataGrid
      ref="smProductTypesGrid"
      :data-source="smProductTypesDataSource"
      :column-min-width="80"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :column-auto-width="true"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :remote-operations="gridRemoteOperations"
      :element-attr="gridAttributes"
      :load-panel="{ enabled: true }"
      :no-data-text="$t('common.no_data_text')"
    >
      <DxColumn
        data-field="name"
        :caption="$t('common.name')"
        data-type="string"
        alignment="left"
        :allow-sorting="true"
        :filter-operations="['contains']"
      />
      <DxColumn
        data-field="created_at"
        :caption="$t('common.created_at')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="getDxGridDefaultDateFormat"
        :width="150"
      />
      <DxColumn
        data-field="updated_at"
        :caption="$t('common.last_update')"
        data-type="date"
        alignment="left"
        :allow-sorting="true"
        :format="updatedAtDateTimeFormat"
        :width="170"
      />
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs.smProductTypesGrid"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :edit-action="onEdit"
        />
      </template>

      <DxEditing mode="row" :allow-updating="false" :allow-adding="false" :allow-deleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: $t('stock_management.product_types.title'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxFilterRow :visible="true" apply-filter="auto" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
      <DxExport :enabled="false" :allow-export-selected-data="false" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomStore from "devextreme/data/custom_store";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxPager,
  DxPaging,
  DxEditing,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxExport,
  DxTexts,
} from "devextreme-vue/data-grid";
import { canDelete, canEdit, canView, permissions, permissionTypes } from "@/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";
import { handleError } from "@/helpers/generalActions";

export default {
  name: "SMProductTypeList",
  middleware: ["auth", "check-company-admin"],
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid",
      },
      smProductTypesDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          const dataSource = {
            data: [],
            totalCount: 0,
          };

          try {
            const pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
            const pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;
            const payload = {
              page: pageNumber,
              limit: pageLimit,
              filter: loadOptions.filter,
              sort: loadOptions.sort,
            };

            const response = await this.getSmProductTypes(payload);
            dataSource.data = response?.data ?? [];
            dataSource.totalCount = response?.pagination?.total ?? 0;
            return dataSource;
          } catch (error) {
            console.error("Error fetching product types:", error);
            throw error;
          }
        },
        remove: this.onDelete,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 50, 100],
    };
  },
  components: {
    Forbidden,
    GridPanelHeader,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxFilterRow,
    DxEditing,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxExport,
    DxActionMenu,
    DxTexts,
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.SM_PRODUCTS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    updatedAtDateTimeFormat() {
      return this.getDxGridDefaultDateFormat ? `${this.getDxGridDefaultDateFormat} HH:mm` : "yyyy-MM-dd HH:mm";
    },
  },
  methods: {
    ...mapActions({
      getSmProductTypes: "smProductTypes/getSmProductTypes",
      deleteSmProductType: "smProductTypes/deleteSmProductType",
    }),
    newObject() {
      this.$router.push({ path: "/stock-management/sm-product-types/add" });
    },
    onEdit(rowData) {
      this.$router.push({ path: `/stock-management/sm-product-types/${rowData.uuid}` });
    },
    onDelete(payload) {
      return this.deleteSmProductType(payload).catch((e) => {
        handleError(this, e);
      });
    },
  },
};
</script>

<style scoped></style>
